import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Company/Site/Device 선택 상태를 전역으로 관리하는 Store
 *
 * 페이지 이동 시에도 선택 상태가 유지되도록 persist 미들웨어 사용
 */
interface SelectionState {
  // 선택된 ID들
  selectedCompanyId: number | 'all' | null;
  selectedSiteId: number | 'all' | null;
  selectedDeviceId: number | 'all' | null;

  // 선택 변경 핸들러
  setSelectedCompanyId: (companyId: number | 'all' | null) => void;
  setSelectedSiteId: (siteId: number | 'all' | null) => void;
  setSelectedDeviceId: (deviceId: number | 'all' | null) => void;

  // 전체 초기화
  reset: () => void;
  resetSite: () => void;
  resetDevice: () => void;
}

const initialState = {
  selectedCompanyId: null as number | 'all' | null,
  selectedSiteId: null as number | 'all' | null,
  selectedDeviceId: null as number | 'all' | null,
};

export const useSelectionStore = create<SelectionState>()(
  persist(
    set => ({
      ...initialState,

      setSelectedCompanyId: (companyId: number | 'all' | null) => {
        // ZUSTAND: 회사 선택 및 하위 선택 초기화
        set({
          selectedCompanyId: companyId,
          // Company 변경 시 Site와 Device 초기화
          selectedSiteId: null,
          selectedDeviceId: null,
        });
      },

      setSelectedSiteId: (siteId: number | 'all' | null) => {
        // ZUSTAND: 사이트 선택 및 디바이스 초기화
        set({
          selectedSiteId: siteId,
          // Site 변경 시 Device 초기화
          selectedDeviceId: null,
        });
      },

      setSelectedDeviceId: (deviceId: number | 'all' | null) => {
        // ZUSTAND: 디바이스 선택
        set({ selectedDeviceId: deviceId });
      },

      reset: () => {
        // ZUSTAND: 전체 선택 초기화
        set(initialState);
      },

      resetSite: () => {
        // ZUSTAND: 사이트/디바이스 선택 초기화
        set({
          selectedSiteId: null,
          selectedDeviceId: null,
        });
      },

      resetDevice: () => {
        // ZUSTAND: 디바이스 선택 초기화
        set({ selectedDeviceId: null });
      },
    }),
    {
      name: 'selection-storage',
      partialize: state => ({
        selectedCompanyId: state.selectedCompanyId,
        selectedSiteId: state.selectedSiteId,
        selectedDeviceId: state.selectedDeviceId,
      }),
      onRehydrateStorage: () => () => {},
    }
  )
);

// Development helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__SELECTION_STORE__ = useSelectionStore;

  (window as any).getSelectionState = () => {
    const state = useSelectionStore.getState();
    console.log('🔍 Selection Store State:', {
      selectedCompanyId: state.selectedCompanyId,
      selectedSiteId: state.selectedSiteId,
      selectedDeviceId: state.selectedDeviceId,
    });
    return state;
  };

  (window as any).watchSelectionStore = () => {
    console.log('👀 Watching selection store changes...');
    return useSelectionStore.subscribe(state => {
      console.log('🔄 [Selection Store Update]', {
        company: state.selectedCompanyId,
        site: state.selectedSiteId,
        device: state.selectedDeviceId,
      });
    });
  };

  // console.log('🛠️ Selection Store Debug Tools Available:');
  // console.log('  - getSelectionState() : 현재 선택 상태 확인');
  // console.log('  - watchSelectionStore() : 실시간 변화 감지');
  // console.log('  - __SELECTION_STORE__.getState() : 전체 상태');
}
